// @ts-check
/**
 * Parser for the "Imported list" audience: pasted or uploaded text with one `name,phone` per line.
 * Runs entirely in the browser. The server still validates every number; this only catches lines
 * that cannot possibly be a recipient, with their line numbers, so the admin can fix them first.
 */

/** Most rows one campaign accepts from a pasted list. */
export const MAX_CSV_ROWS = 2000;

const PHONE_CHARS = /^\+?[\d\s().-]+$/;
const HEADER_WORDS = /phone|name|mobile|number/i;

/** Digits, spaces, dashes, dots and brackets with an optional leading +, and 6 to 15 digits. */
const looksLikePhone = (/** @type {string} */ value) => {
  if (!PHONE_CHARS.test(value)) return false;
  const digits = value.replace(/\D/g, '').length;
  return digits >= 6 && digits <= 15;
};

/**
 * Splits one line on commas, honouring double quotes ("Rao, Asha") and doubled quotes inside them.
 * @param {string} line
 * @returns {string[]} trimmed fields
 */
const splitLine = (line) => {
  const fields = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      fields.push(field.trim());
      field = '';
    } else field += ch;
  }
  fields.push(field.trim());
  return fields;
};

/**
 * @param {string | null | undefined} text
 * @returns {{rows: {name: string, phone: string}[], errors: {line: number, message: string}[], tooMany: boolean}}
 *   line numbers are 1-based positions in the text, header and blank lines included; tooMany is true
 *   when the list passed MAX_CSV_ROWS (rows then holds only the first MAX_CSV_ROWS).
 */
export function parseCsvAudience(text) {
  const rows = [];
  const errors = [];
  let tooMany = false;
  let seenFirst = false;

  String(text ?? '')
    .replace(/^\uFEFF/, '')
    .split(/\r\n|\n|\r/)
    .forEach((raw, index) => {
      if (!raw.trim()) return;
      const line = index + 1;
      const fields = splitLine(raw);
      const isFirst = !seenFirst;
      seenFirst = true;

      const last = fields[fields.length - 1];
      if (isFirst && fields.length <= 2 && !looksLikePhone(last) && HEADER_WORDS.test(raw)) return; // header

      if (fields.length > 2) {
        errors.push({ line, message: 'Too many columns: expected name,phone' });
        return;
      }
      const [name, phone] = fields.length === 2 ? fields : ['', fields[0]];
      if (!looksLikePhone(phone)) {
        errors.push({ line, message: 'No valid phone number' });
        return;
      }
      if (rows.length >= MAX_CSV_ROWS) {
        tooMany = true;
        return;
      }
      rows.push({ name, phone });
    });

  return { rows, errors, tooMany };
}
